import { AUTHTOKEN, ISACTIVE, isSubscribed, USER } from '@/constants';
import { HTTP } from '@/http-common';
import { defineStore } from 'pinia';

export const useBokStore = defineStore('bok', {
    state: () => ({
        packages: [],
    }),
    getters: {
        getPlans: (state) => state.packages,
    },
    actions: {
        getBokIdempotencyStorageKeys() {
            return {
                INIT: 'bok-idempotency-init',
                CAPTURE: 'bok-idempotency-capture',
                RESEND: 'bok-idempotency-resend',
            };
        },
        parseUserData() {
            try {
                return JSON.parse(localStorage.getItem('user-data'));
            } catch (error) {
                return null;
            }
        },
        createIdempotencyKey(prefix = 'bok') {
            const randomUuid =
                typeof window !== 'undefined' &&
                window.crypto &&
                typeof window.crypto.randomUUID === 'function'
                    ? window.crypto.randomUUID()
                    : null;
            if (randomUuid) {
                return `${prefix}-${randomUuid}`;
            }

            return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        },
        buildIdempotencyRequest(options = {}, prefix = 'bok') {
            const providedKey = options?.idempotencyKey || options?.headers?.['Idempotency-Key'];
            const idempotencyKey = providedKey || this.createIdempotencyKey(prefix);

            return {
                idempotencyKey,
                config: {
                    ...(options?.config || {}),
                    headers: {
                        ...(options?.headers || {}),
                        'Idempotency-Key': idempotencyKey,
                    },
                },
            };
        },
        clearBokIdempotencyKeys() {
            const keys = this.getBokIdempotencyStorageKeys();
            localStorage.removeItem(keys.INIT);
            localStorage.removeItem(keys.CAPTURE);
            localStorage.removeItem(keys.RESEND);
        },
        /**
         * This function initiates a payment by sending a POST request to the '/bok/initiate' endpoint with the provided payload.
         * @param {Object} payload - The data to be sent in the request body.
         * @param {Object} options - Request options (headers/idempotencyKey).
         * @returns {Promise<Object>} - A promise that resolves to the response data from the server.
         * @throws {Error} - Throws an error if the request fails.
         */
        async initPayment(payload, options = {}) {
            try {
                const user = this.parseUserData();
                payload.user_id = user?.id;
                const { config } = this.buildIdempotencyRequest(options, 'bok-init');
                const response = await HTTP.post('/bok/initiate', payload, config);
                //save data to local storage
                localStorage.setItem('trx_id', response?.data?.id);
                return response;
            } catch (error) {
                console.error('Error initiating payment:', error);
                throw error;
            }
        },
        /**
         * 
         * @param {Object} payload - The data to be sent in the request body.
         * @param {Object} options - Request options (headers/idempotencyKey).
         * @returns {Promise<Object>} - A promise that resolves to the response data from the server.
         * @throws {Error} - Throws an error if the request fails.
         */
        async verifySubscription(payload, options = {}) {
            try {
                const user = this.parseUserData();
                payload.user_id = user?.id;
                const trx_id = localStorage.getItem('trx_id');
                payload.trx_id = trx_id;
                const { config } = this.buildIdempotencyRequest(options, 'bok-capture');
                const response = await HTTP.post('/bok/capture', payload, config);
                window.localStorage.setItem(USER, JSON.stringify(response.data.user));
                window.localStorage.setItem(AUTHTOKEN, response.data.token);
                window.localStorage.setItem(ISACTIVE, response.data.token ? true : false);
                localStorage.setItem(isSubscribed, true);

                return response;
            } catch (error) {
                console.error('Error verifying subscription:', error);
                throw error;
            }
        },

        async resendSubscriptionOtp(payload = {}, options = {}) {
            const user = this.parseUserData();
            const mobile = payload.mobile || localStorage.getItem('phone-bok');
            const cif = payload.cif || localStorage.getItem('cif-bok');
            const plan_uuid = payload.plan_uuid || localStorage.getItem('bok-plan-uuid');
            const { config } = this.buildIdempotencyRequest(options, 'bok-resend');

            if (!mobile || !cif || !plan_uuid) {
                throw new Error('Missing required BOK payment context to resend OTP.');
            }

            const initiatePayload = {
                cif,
                plan_uuid,
                mobile,
                user_id: user?.id,
                ...payload,
            };

            const response = await HTTP.post('/bok/initiate', initiatePayload, config);
            const nextTrxId = response?.data?.id || response?.data?.trx_id;
            if (nextTrxId) {
                localStorage.setItem('trx_id', nextTrxId);
            }

            return response;
        },

        async fetchPlans() {
            try {
                const response = await HTTP.get('/bok/plans');
                this.packages = response.data;
            } catch (error) {
                console.error('Error fetching plans:', error);
                throw error;
            }
        }
    }

});
